import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import type { Rating } from '@eprintsmart/shared';
import { moderateRating, watchRatings } from '../services/ratings';

/**
 * Every rating given, and the ability to take an abusive one down.
 *
 * REMOVAL IS FOR ABUSE, NOT FOR OUTCOMES. IS 19000:2022 turns on no selective
 * publication: a platform may not suppress a rating because it dislikes the
 * score. So a reason is mandatory, the row is marked rather than deleted, and
 * a removed rating drops out of the average while staying in the count — the
 * number of people who rated somebody is not ours to revise.
 *
 * The dialog says all of that at the point of pressing the button, because
 * that is where somebody is deciding, not in a policy document.
 */

export function Ratings() {
  const [rows, setRows] = useState<Rating[]>([]);
  const [removing, setRemoving] = useState<Rating | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchRatings(setRows), []);

  const counts = useMemo(() => ({
    total: rows.length,
    removed: rows.filter((r) => r.status === 'REMOVED').length,
    low: rows.filter((r) => r.status === 'PUBLISHED' && r.score <= 2).length,
  }), [rows]);

  const act = async (rating: Rating, action: 'REMOVE' | 'RESTORE', why: string) => {
    setBusy(true);
    setError(null);
    try {
      await moderateRating({ ratingId: rating.id, action, reason: why });
      setRemoving(null);
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Box>
        <Typography variant="h6" gutterBottom>Ratings</Typography>
        <Stack direction="row" spacing={3} mb={2}>
          <Box>
            <Typography variant="h4">{counts.total}</Typography>
            <Typography variant="caption" color="text.secondary">given</Typography>
          </Box>
          <Box>
            <Typography variant="h4" color={counts.low ? 'warning.main' : 'text.primary'}>
              {counts.low}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              at 1–2 stars &middot; worth reading, not removing
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4">{counts.removed}</Typography>
            <Typography variant="caption" color="text.secondary">removed for abuse</Typography>
          </Box>
        </Stack>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Every rating comes from a real transaction — an approved commission, or a delivered order
          carrying that artist&rsquo;s design. Remove one only for a personal attack, a threat, or
          content identifying somebody. A low score is not a reason.
        </Typography>

        <Paper variant="outlined">
          {rows.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                Nothing rated yet. Ratings appear once a commission is approved or an order
                carrying a design is delivered.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>About</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} sx={r.status === 'REMOVED' ? { opacity: 0.55 } : undefined}>
                    <TableCell>
                      <Chip size="small" variant="outlined"
                        label={r.subjectType === 'designer' ? 'designer' : 'customer'} />
                      <Typography variant="caption" display="block" color="text.secondary">
                        {r.subjectId}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.authorName}</TableCell>
                    <TableCell align="right">
                      <span style={{ color: r.score <= 2 ? '#c62828' : '#f5a623' }}>
                        {'★'.repeat(r.score)}{'☆'.repeat(5 - r.score)}
                      </span>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2">{r.comment ?? '—'}</Typography>
                      {r.moderation && (
                        <Typography variant="caption" color="error">
                          removed: {r.moderation.reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.sourceType === 'commission' ? 'commission' : 'purchase'}
                        {r.visibility === 'designers' && ' · private'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {r.status === 'PUBLISHED' ? (
                        <Button size="small" color="error" disabled={busy}
                          onClick={() => { setRemoving(r); setReason(''); }}>
                          Remove
                        </Button>
                      ) : (
                        <Button size="small" disabled={busy}
                          onClick={() => void act(r, 'RESTORE', 'Restored after review.')}>
                          Put back
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)} fullWidth maxWidth="sm">
        <DialogTitle>Remove this rating</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Only for abuse — a personal attack, a threat, or content identifying somebody. Not
            because the score is low. The rating stays in the count and only drops out of the
            average, so this cannot be used to improve anyone&rsquo;s standing.
          </Alert>
          <TextField
            label="Why is this being removed?"
            fullWidth multiline rows={3} value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Recorded against the rating, and readable later if anyone asks."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy || reason.trim().length < 3}
            onClick={() => removing && void act(removing, 'REMOVE', reason.trim())}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
